/**
 * The furniture of a how-to: the chapter heading over the stage, the line of explanation
 * under it, the labels drawn out to the part they name, and the few tools a lesson holds
 * up — a tape across two points, a level with its bubble, a tick and a cross.
 *
 * A how-to has no narrator: it is captions only. So its words are larger and stay longer
 * than a film's caption, sit on a card of paper so they read over any scene, and never
 * leave the stage square — the heading at its top, the line at its bottom, the action
 * between them, in every shape a recording is made at.
 *
 * Like every prop, each is drawn from where it is and how far through its motion it is,
 * with no state.
 */

import type { Ink, Pt } from "./ink.js";
import type { Mix } from "./sound.js";
import { clamp, ease, mix, span } from "./timing.js";

/** Where the heading's title sits once it has settled at the top of the stage. */
const HEADING_Y = -372;
/** The middle of the line of explanation at the bottom of the stage. */
const SAY_Y = 352;
/** How wide a line of explanation may set: the stage, less a margin either side. */
const SAY_WIDTH = 800;

/** A chapter of a how-to: its number and its title, as the heading and the description name it. */
export interface Chapter {
  /** When it starts, in the film's seconds. */
  at: number;
  number: number;
  title: string;
}

/**
 * A chapter's heading: it lands big in the middle of the stage, holds a moment, and
 * rises to the top, where it stays until the chapter ends.
 */
export function heading(ink: Ink, chapter: Chapter, t: number, end: number): void {
  if (t < chapter.at || t > end) {
    return;
  }
  const p = ink.palette;
  const u = t - chapter.at;
  const pop = ease.back(span(u, 0, 0.4));
  const rise = ease.inOut(span(u, 1.5, 2.2));
  const out = span(t, end - 0.25, end);
  const alpha = clamp(span(u, 0, 0.12) - out);
  const size = mix(78, 46, rise);
  const y = mix(-40, HEADING_Y, rise);
  const { ctx } = ink;
  ctx.save();
  ctx.translate(0, y);
  ctx.scale(mix(0.6, 1, pop), mix(0.6, 1, pop));
  ctx.rotate(-0.012);
  // A card behind it while it holds the middle, so it reads over the scene it introduces.
  const card = 1 - rise;
  const style = { size, weight: 900, alpha };
  const width = ink.measure(chapter.title, style);
  if (card > 0.02) {
    ink.rect(-width / 2 - 40, -size * 1.45, width + 80, size * 2.5, {
      fill: p.trim,
      fillAlpha: 0.94 * alpha * card,
      width: 3,
      alpha: alpha * card,
      offset: 5,
    });
  }
  ink.text(`CHAPTER ${chapter.number}`, 0, -size * 0.92, {
    size: size * 0.32,
    weight: 800,
    color: p.accent,
    tracking: 0.2,
    alpha,
  });
  ink.text(chapter.title, 0, 0, style);
  const draw = ease.out(span(u, 0.2, 0.6));
  if (draw > 0) {
    ink.curve(
      [
        { x: -width / 2 - 6, y: size * 0.62 },
        { x: 0, y: size * 0.52 },
        { x: width / 2 + 10, y: size * 0.6 },
      ],
      { width: mix(8, 6, rise), color: p.accent, alpha, draw },
    );
  }
  ctx.restore();
}

/** A line of explanation, and when it is on: in the film's seconds. */
export interface Line {
  text: string;
  start: number;
  end: number;
  /** A word or two in it drawn in the accent: the price, or the point of the line. */
  accent?: string;
}

/**
 * A line of explanation at the bottom of the stage, on a card of paper: wrapped to two
 * lines at most, it fades up, holds and fades down — words to be read, not to pop.
 */
export function say(ink: Ink, line: Line, t: number): void {
  if (t < line.start || t > line.end + 0.3) {
    return;
  }
  const p = ink.palette;
  const style = { size: 38, weight: 800 };
  const rows = wrapped(ink, line.text, SAY_WIDTH, style);
  const alpha = clamp(span(t, line.start, line.start + 0.25) - span(t, line.end, line.end + 0.3));
  const lift = (1 - ease.out(span(t, line.start, line.start + 0.35))) * 14;
  const leading = 46;
  const widest = Math.max(...rows.map((row) => ink.measure(row, style)));
  const height = rows.length * leading + 26;
  const top = SAY_Y - height / 2 + lift;
  ink.rect(-widest / 2 - 28, top, widest + 56, height, {
    fill: p.trim,
    fillAlpha: 0.92 * alpha,
    width: 2.4,
    alpha,
    offset: 4,
    overshoot: false,
  });
  rows.forEach((row, i) => {
    const y = top + 13 + leading * (i + 0.5);
    if (line.accent !== undefined && row.includes(line.accent)) {
      const [before = "", after = ""] = row.split(line.accent);
      let x = -ink.measure(row, style) / 2;
      for (const [part, color] of [
        [before, p.ink],
        [line.accent, p.accent],
        [after, p.ink],
      ] as const) {
        ink.text(part, x, y, { ...style, align: "left", color, alpha });
        x += ink.measure(part, style);
      }
    } else {
      ink.text(row, 0, y, { ...style, alpha });
    }
  });
}

/** Words broken into rows no wider than `width` in a style. */
export function wrapped(ink: Ink, text: string, width: number, style: { size: number; weight: number }): string[] {
  const rows: string[] = [];
  let row = "";
  for (const word of text.split(" ")) {
    const next = row === "" ? word : `${row} ${word}`;
    if (ink.measure(next, style) > width && row !== "") {
      rows.push(row);
      row = word;
    } else {
      row = next;
    }
  }
  if (row !== "") {
    rows.push(row);
  }
  return rows;
}

/**
 * A label drawn out to the part it names: a dot on the part, a leader to the words, and
 * the words on a tab of paper. `k` draws it on, 0–1.
 */
export function label(ink: Ink, text: string, part: Pt, at: Pt, k: number, size = 26): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  const alpha = clamp(k * 3);
  ink.dot(part.x, part.y, 4.5, p.accent, alpha);
  ink.curve(
    [
      part,
      { x: mix(part.x, at.x, 0.5), y: mix(part.y, at.y, 0.5) - 6 },
      at,
    ],
    { width: 2.2, alpha, draw: ease.out(clamp(k * 1.6)) },
  );
  const style = { size, weight: 800 };
  const width = ink.measure(text, style);
  const shown = ease.back(span(k, 0.45, 1));
  if (shown <= 0) {
    return;
  }
  const { ctx } = ink;
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.scale(shown, shown);
  ink.rect(-width / 2 - 12, -size * 0.8, width + 24, size * 1.6, { fill: p.trim, width: 2.2, offset: 3, overshoot: false });
  ink.text(text, 0, 1, style);
  ctx.restore();
}

/**
 * A tape measure pulled between two points: the line drawn out from `a`, ticks along
 * it, and a tag at its middle — a letter, never a figure from a plan.
 */
export function tape(ink: Ink, a: Pt, b: Pt, k: number, tag: string, color = ink.palette.accent, tagAt = 0.3): void {
  if (k <= 0) {
    return;
  }
  const end = { x: mix(a.x, b.x, ease.out(k)), y: mix(a.y, b.y, ease.out(k)) };
  ink.line(a.x, a.y, end.x, end.y, { width: 5, color, overshoot: false, wobble: 0.4 });
  const length = Math.hypot(end.x - a.x, end.y - a.y);
  const ux = (end.x - a.x) / Math.max(1, length);
  const uy = (end.y - a.y) / Math.max(1, length);
  for (let d = 14; d < length - 6; d += 14) {
    const x = a.x + ux * d;
    const y = a.y + uy * d;
    ink.line(x - uy * 4, y + ux * 4, x + uy * 4, y - ux * 4, { width: 1.2, overshoot: false, wobble: 0.2 });
  }
  ink.rect(end.x - 6, end.y - 6, 12, 12, { fill: color, width: 1.6, offset: 0, overshoot: false });
  if (k >= 0.9 && tag !== "") {
    // A third of the way along by default, so the tags of two diagonals do not meet where they cross.
    const mid = { x: mix(a.x, b.x, tagAt), y: mix(a.y, b.y, tagAt) };
    ink.circle(mid.x, mid.y, 18, { fill: ink.palette.trim, width: 2.4, offset: 1 });
    ink.text(tag, mid.x, mid.y + 1, { size: 22, weight: 900, color });
  }
}

/** A spirit level `length` long, lying from `at` at `angle`: its bubble off by `off`, −1 to 1. */
export function level(ink: Ink, at: Pt, length: number, angle: number, off: number): void {
  const p = ink.palette;
  const { ctx } = ink;
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.rotate(angle);
  ink.rect(0, -16, length, 16, { fill: p.accent, width: 2.4, offset: 2 });
  const vial = { x: length / 2, w: 44 };
  ink.rect(vial.x - vial.w / 2, -13, vial.w, 10, { fill: p.glass, width: 1.6, offset: 0, overshoot: false });
  ink.line(vial.x - 7, -13, vial.x - 7, -3, { width: 1.2, overshoot: false });
  ink.line(vial.x + 7, -13, vial.x + 7, -3, { width: 1.2, overshoot: false });
  ink.ellipse(vial.x + clamp(off, -1, 1) * 15, -8, 6, 3.5, { fill: p.trim, width: 1.2, offset: 0 });
  ctx.restore();
}

/** A tick, drawn on with `k`: this one is right. */
export function tick(ink: Ink, x: number, y: number, k: number, size = 30): void {
  if (k <= 0) {
    return;
  }
  ink.poly(
    [
      { x: x - size * 0.5, y: y },
      { x: x - size * 0.12, y: y + size * 0.4 },
      { x: x + size * 0.6, y: y - size * 0.55 },
    ],
    false,
    { width: 7, color: ink.palette.accent, draw: ease.out(k), overshoot: false },
  );
}

/** A cross, drawn on with `k`: this one is wrong. */
export function cross(ink: Ink, x: number, y: number, k: number, size = 26): void {
  if (k <= 0) {
    return;
  }
  const color = ink.palette.signal;
  ink.line(x - size / 2, y - size / 2, mix(x - size / 2, x + size / 2, ease.out(clamp(k * 2))), mix(y - size / 2, y + size / 2, ease.out(clamp(k * 2))), {
    width: 7,
    color,
    overshoot: false,
  });
  const second = clamp(k * 2 - 1);
  if (second > 0) {
    ink.line(x + size / 2, y - size / 2, mix(x + size / 2, x - size / 2, ease.out(second)), mix(y - size / 2, y + size / 2, ease.out(second)), {
      width: 7,
      color,
      overshoot: false,
    });
  }
}

/** A fat arrow pointing down: a load on its way to the ground. `k` grows it. */
export function arrowDown(ink: Ink, x: number, y: number, length: number, k: number, color = ink.palette.signal): void {
  if (k <= 0) {
    return;
  }
  const reach = length * ease.out(k);
  const head = Math.min(18, reach * 0.5);
  ink.line(x, y, x, y + reach - head * 0.6, { width: 7, color, overshoot: false });
  ink.shape(
    [
      { x: x - head * 0.8, y: y + reach - head },
      { x: x + head * 0.8, y: y + reach - head },
      { x, y: y + reach },
    ],
    { fill: color, width: 2, color, offset: 0 },
  );
}

/** Rain: streaks falling across the view, seeded by their column, never at random. */
export function rain(ink: Ink, left: number, right: number, top: number, bottom: number, u: number, k: number): void {
  if (k <= 0) {
    return;
  }
  const fall = bottom - top;
  for (let i = 0; i < 46; i += 1) {
    const x = left + ((i * 137.5) % (right - left));
    const offset = (i * 61.8) % fall;
    const y = top + ((offset + u * 620) % fall);
    ink.line(x, y, x - 5, y + 20, { width: 2.2, color: ink.palette.roof, alpha: 0.55 * k, overshoot: false, wobble: 0.2 });
  }
}

/** A breath of air under each line of explanation as it comes up, and the page turned at each wipe. */
export function lessonSound(mixer: Mix, lines: readonly Line[], headings: readonly Chapter[], wipes: readonly number[]): void {
  for (const line of lines) {
    mixer.whoosh(line.start - 0.02, 0.35, { from: 700, to: 2600, gain: 0.12 });
  }
  for (const chapter of headings) {
    mixer.whoosh(chapter.at + 1.5, 0.6, { from: 1800, to: 600, gain: 0.14 });
  }
  for (const at of wipes) {
    mixer.whoosh(at - 0.28, 0.56, { from: 500, to: 2400, gain: 0.42 });
  }
}
